import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Dialog, DialogContent } from '@material-ui/core'
import { Button } from '@material-ui/core'
import { Add, CloudUpload, Close } from '@material-ui/icons'
import { Image, Spinner } from 'ui'
import { observer } from 'mobx-react'
import useStore from 'store'

import {
  DialogHeader,
  Header,
  StyledIconButton,
  CategoryListTitleContainer,
  CategoryListTitleSubContainer1,
  CategoryListTitleSubContainer2,
  HeaderTitle1,
  HeaderTitle2,
  ImageHeader,
  NewCategoryBody,
  NewCategoryContainer,
  NewSubCategoryContainer,
  NewCategoryContainerColumn,
  NewCategoryContainerActions,
} from './CategoryModal.styles'

import { FormInput, FormSelect } from './FormComponents'

import FileIcon from 'assets/icons/ims/File.svg'
import DeleteIcon from 'assets/icons/ims/Delete.svg'

// ─── local styled components ────────────────────────────────────────────────

const SectionLabel = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  color: #101828;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`

const AddRowButton = styled(Button)`
  color: #155DFC;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  min-width: 0;
  text-transform: none;
`

const ListRow = styled.div`
  display: flex;
  width: 100%;
  gap: 12px;
  align-items: center;
`

const StyledTextarea = styled.textarea`
  display: flex;
  width: 100%;
  min-height: 140px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  color: rgba(15, 23, 42, 0.70);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  direction: rtl;
`

const StyledDateInput = styled.input`
  display: flex;
  height: 41.804px;
  padding: 8px 16px;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  color: rgba(15, 23, 42, 0.70);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 400;
  outline: none;
  width: 100%;
  box-sizing: border-box;
`

const FieldLabel = styled.div`
  color: #364153;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  text-align: right;
  width: 100%;
`

const FieldRequired = styled.span`
  color: #364153;
  margin-left: 2px;
`

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  align-self: stretch;
  width: 100%;
`

const UploadZone = styled.label`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px 16px;
  border-radius: 8px;
  border: 1.5px dashed #D1D5DC;
  background: #F8FAFC;
  cursor: pointer;
  box-sizing: border-box;
  &:hover {
    border-color: #155DFC;
    background: #EFF6FF;
  }
`

const UploadText = styled.div`
  color: #64748B;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  text-align: center;
`

const UploadFileName = styled.div`
  color: #155DFC;
  font-family: Rubik;
  font-size: 13px;
  font-weight: 400;
  text-align: center;
  margin-top: 4px;
`

const HiddenInput = styled.input`
  display: none;
`

const SaveDraftButton = styled(Button)`
  display: flex;
  padding: 8.724px 23.631px 7.261px 23.992px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #155DFC;
  color: #FFF;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`

const SendApprovalButton = styled(Button)`
  display: flex;
  padding: 8.724px 23.631px 7.261px 23.992px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  background: #00A63E;
  color: #FFF;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`

const CancelButton = styled(Button)`
  display: flex;
  padding: 9.634px 23.98px 8.17px 24.901px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  background: #FFF;
  color: #364153;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
`

const ActionsRow = styled(NewCategoryContainerActions)`
  padding-right: 0;
  gap: 11.989px;
`
const ActionsImage = styled(Image)`
  width: 16px;
  height: 16px;
`

// ─── types ───────────────────────────────────────────────────────────────────

type Participant = { userId: string; role: string }

// ─── component ───────────────────────────────────────────────────────────────

const ProtocolModal = ({ open, onCloseModal, onSaved }: { open: boolean; onCloseModal: (v: boolean) => void; onSaved?: () => void }) => {
  const { orgs, protocolsStore } = useStore()

  const [title, setTitle] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [date, setDate] = useState('')
  const [participants, setParticipants] = useState<Participant[]>([{ userId: '', role: '' }])
  const [topics, setTopics] = useState<string[]>([''])
  const [decisions, setDecisions] = useState<string[]>([''])
  const [content, setContent] = useState('')
  const [mainFile, setMainFile] = useState<File | null>(null)
  const [attachments, setAttachments] = useState<File[]>([])
  const [saving, setSaving] = useState(false)

  const mainFileRef = useRef<HTMLInputElement>(null)
  const attachmentsRef = useRef<HTMLInputElement>(null)

  const resetForm = () => {
    setTitle('')
    setCategoryId('')
    setDate('')
    setParticipants([{ userId: '', role: '' }])
    setTopics([''])
    setDecisions([''])
    setContent('')
    setMainFile(null)
    setAttachments([])
  }

  const handleClose = () => {
    resetForm()
    onCloseModal(false)
  }

  useEffect(() => {
    if (open) {
      protocolsStore.loadUserList()
      protocolsStore.loadCategories(orgs.currentOrgId)
    }
  }, [open])

  const userOptions = [
    { value: '', label: 'בחר משתתף' },
    ...protocolsStore.userList.map(u => ({ value: String(u.userId), label: u.fullName })),
  ]

  // participants
  const updateParticipant = (index: number, field: keyof Participant, value: string) => {
    setParticipants(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p))
  }
  const addParticipant = () => setParticipants(prev => [...prev, { userId: '', role: '' }])
  const removeParticipant = (index: number) => setParticipants(prev => prev.filter((_, i) => i !== index))

  // topics
  const updateTopic = (index: number, value: string) => setTopics(prev => prev.map((t, i) => i === index ? value : t))
  const addTopic = () => setTopics(prev => [...prev, ''])
  const removeTopic = (index: number) => setTopics(prev => prev.filter((_, i) => i !== index))

  // decisions
  const updateDecision = (index: number, value: string) => setDecisions(prev => prev.map((d, i) => i === index ? value : d))
  const addDecision = () => setDecisions(prev => [...prev, ''])
  const removeDecision = (index: number) => setDecisions(prev => prev.filter((_, i) => i !== index))

  const buildBody = (statusId: number) => ({
    title,
    categoryId: Number(categoryId),
    protocolDate: new Date(date).toISOString(),
    protocolStatusId: statusId,
    participants: participants
      .filter(p => p.userId)
      .map(p => ({ userId: Number(p.userId), participantRole: p.role })),
    topics: topics.filter(Boolean).join('\n'),
    decisions: decisions.filter(Boolean).join('\n'),
    description: content,
  })

  const submitProtocol = async (statusId: number) => {
    setSaving(true)
    const resId = await protocolsStore.createProtocol(buildBody(statusId))

    if (resId) {
      const allFiles = [
        ...(mainFile ? [{ file: mainFile, isMainFile: true }] : []),
        ...attachments.map(file => ({ file, isMainFile: false })),
      ]
      if (allFiles.length > 0 && resId) {
        await protocolsStore.uploadProtocolFiles(resId, allFiles)
      }

      handleClose()
      onSaved?.()
    }

    setSaving(false)
  }

  const handleSaveDraft = () => submitProtocol(1)
  const handleSendApproval = () => submitProtocol(2)

  return (
    <Dialog
      style={{ zIndex: 1200 }}
      open={open}
      fullWidth
      maxWidth="lg"
      scroll="body"
    >
      <DialogHeader>
        <Header>
          <CategoryListTitleContainer>
            <CategoryListTitleSubContainer2>
              <ImageHeader src={FileIcon} />
            </CategoryListTitleSubContainer2>
            <CategoryListTitleSubContainer1>
              <HeaderTitle1>יצירת פרוטוקול חדש</HeaderTitle1>
              <HeaderTitle2>מלא את פרטי הפרוטוקול</HeaderTitle2>
            </CategoryListTitleSubContainer1>
          </CategoryListTitleContainer>
          <StyledIconButton onClick={handleClose}><Close style={{ color: '#364153' }} /></StyledIconButton>
        </Header>
      </DialogHeader>

      <DialogContent>
        <NewCategoryBody>

          {/* Row 1 — title + category */}
          <NewCategoryContainer>
            <NewSubCategoryContainer style={{ width: '50%', height: 'auto' }}>
              <FormInput
                label="כותרת הפרוטוקול"
                required
                value={title}
                onChange={setTitle}
                placeholder="לדוגמה: סיכום אסיפה כללית - ינואר 2024"
              />
            </NewSubCategoryContainer>
            <NewSubCategoryContainer style={{ width: '50%', height: 'auto' }}>
              <FormSelect
                label="קטגוריה"
                required
                value={categoryId}
                onChange={setCategoryId}
                options={[
                  { value: '', label: 'בחר קטגוריה' },
                  ...protocolsStore.categories.map(c => ({ value: String(c.id), label: c.categoryName })),
                ]}
              />
            </NewSubCategoryContainer>
          </NewCategoryContainer>

          {/* Row 2 — date */}
          <NewCategoryContainerColumn>
            <FieldWrapper>
              <FieldLabel>תאריך הפרוטוקול<FieldRequired>*</FieldRequired></FieldLabel>
              <StyledDateInput
                type="date"
                value={date}
                max={new Date().toISOString().split('T')[0]}
                onChange={e => setDate(e.target.value)}
                placeholder="dd/mm/yyyy"
              />
            </FieldWrapper>
          </NewCategoryContainerColumn>

          {/* Row 3 — participants */}
          <NewCategoryContainerColumn>
            <SectionLabel>
              <span>משתתפים</span>
              <AddRowButton startIcon={<Add />} onClick={addParticipant}>הוסף משתתף</AddRowButton>
            </SectionLabel>
            {participants.map((p, i) => (
              <ListRow key={i}>
                <div style={{ flex: 2 }}>
                  <FormSelect
                    label=""
                    value={p.userId}
                    onChange={v => updateParticipant(i, 'userId', v)}
                    options={userOptions}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <FormInput
                    label=""
                    value={p.role}
                    onChange={v => updateParticipant(i, 'role', v)}
                    placeholder="תפקיד (אופציונלי)"
                  />
                </div>
                {participants.length > 1 && (
                  <StyledIconButton onClick={() => removeParticipant(i)}>
                    <ActionsImage src={DeleteIcon} />
                  </StyledIconButton>
                )}
              </ListRow>
            ))}
          </NewCategoryContainerColumn>

          {/* Row 4 — topics */}
          <NewCategoryContainerColumn>
            <SectionLabel>
              <span>נושאים לדיון</span>
              <AddRowButton startIcon={<Add />} onClick={addTopic}>הוסף נושא</AddRowButton>
            </SectionLabel>
            {topics.map((t, i) => (
              <ListRow key={i}>
                <div style={{ flex: 1 }}>
                  <FormInput
                    label=""
                    value={t}
                    onChange={v => updateTopic(i, v)}
                    placeholder="נושא לדיון"
                  />
                </div>
                {topics.length > 1 && (
                  <StyledIconButton onClick={() => removeTopic(i)}>
                    <ActionsImage src={DeleteIcon} />
                  </StyledIconButton>
                )}
              </ListRow>
            ))}
          </NewCategoryContainerColumn>

          {/* Row 5 — decisions */}
          <NewCategoryContainerColumn>
            <SectionLabel>
              <span>החלטות</span>
              <AddRowButton startIcon={<Add />} onClick={addDecision}>הוסף החלטה</AddRowButton>
            </SectionLabel>
            {decisions.map((d, i) => (
              <ListRow key={i}>
                <div style={{ flex: 1 }}>
                  <FormInput
                    label=""
                    value={d}
                    onChange={v => updateDecision(i, v)}
                    placeholder="החלטה"
                  />
                </div>
                {decisions.length > 1 && (
                  <StyledIconButton onClick={() => removeDecision(i)}>
                    <ActionsImage src={DeleteIcon} />
                  </StyledIconButton>
                )}
              </ListRow>
            ))}
          </NewCategoryContainerColumn>

          {/* Row 6 — content */}
          <NewCategoryContainerColumn>
            <FieldWrapper>
              <FieldLabel>תוכן הפרוטוקול</FieldLabel>
              <StyledTextarea
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder="הקלד את תוכן הפרוטוקול כאן..."
                dir="rtl"
              />
            </FieldWrapper>
          </NewCategoryContainerColumn>

          {/* Row 7 — main file upload */}
          <NewCategoryContainerColumn>
            <FieldWrapper>
              <FieldLabel>קובץ פרוטוקול ראשי (PDF, DOC, DOCX)</FieldLabel>
              <UploadZone>
                <HiddenInput
                  ref={mainFileRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={e => setMainFile(e.target.files?.[0] || null)}
                />
                <CloudUpload style={{ color: '#94A3B8', fontSize: 32 }} />
                <UploadText>לחץ להעלאת קובץ או גרור לכאן</UploadText>
                {mainFile && <UploadFileName>{mainFile.name}</UploadFileName>}
              </UploadZone>
            </FieldWrapper>
          </NewCategoryContainerColumn>

          {/* Row 8 — attachments upload */}
          <NewCategoryContainerColumn>
            <FieldWrapper>
              <FieldLabel>קבצים מצורפים (ניתן להעלות מספר קבצים)</FieldLabel>
              <UploadZone>
                <HiddenInput
                  ref={attachmentsRef}
                  type="file"
                  multiple
                  onChange={e => setAttachments(Array.from(e.target.files || []))}
                />
                <CloudUpload style={{ color: '#94A3B8', fontSize: 32 }} />
                <UploadText>לחץ להעלאת קבצים או גרור לכאן</UploadText>
                {attachments.length > 0 && (
                  <UploadFileName>{attachments.map(f => f.name).join(', ')}</UploadFileName>
                )}
              </UploadZone>
            </FieldWrapper>
          </NewCategoryContainerColumn>

          <ActionsRow>
            <CancelButton onClick={handleClose}>ביטול</CancelButton>
            <SaveDraftButton onClick={handleSaveDraft} disabled={saving || !title.trim()}>
              {saving ? <Spinner size={16} /> : 'שלח לאישור'}
            </SaveDraftButton>
            <SendApprovalButton onClick={handleSendApproval} disabled={saving || !title.trim() || !categoryId || !date}>
              {saving ? <Spinner size={16} /> : 'שמור כטיוטה'}
            </SendApprovalButton>
          </ActionsRow>
        </NewCategoryBody>
      </DialogContent>
    </Dialog>
  )
}

export default observer(ProtocolModal)
